import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import ImportaDaSocietaModal, { SocietaSorgente, EsitoImport } from '../../components/ImportaDaSocietaModal';

interface AiSorgente {
  id: number;
  provider: string;
  modello: string;
  attivo: boolean;
  note: string | null;
}

// SVG Icons
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const Edit2Icon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const ZapIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
);
const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);
const XCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line>
  </svg>
);

interface ConfigurazioneAI {
  id: number;
  societaId: number;
  provider: string;
  modello: string;
  secretManagerRef: string;
  attivo: boolean;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  /** Altre configurazioni (di altre società) che usano la stessa chiave API. */
  condivisoCon?: number;
}

interface ModalData {
  id?: number;
  provider: string;
  modello: string;
  apiKey: string;
  note: string;
  attivo: boolean;
}

interface LogEntry {
  id: number;
  societaId: number;
  provider: string;
  modello: string;
  contestoTipo: string;
  contestoId: number;
  promptTokens: number | null;
  completionTokens: number | null;
  durataMs: number | null;
  esitoOk: boolean;
  erroreMessaggio: string | null;
  createdAt: string;
}

interface TestResult {
  ok: boolean;
  latenzaMs: number;
  risposta?: string;
  errore?: string;
}

const PROVIDER_OPTIONS = [
  { value: 'GEMINI', label: 'Google Gemini' },
  { value: 'OPENAI', label: 'OpenAI' },
  { value: 'ANTHROPIC', label: 'Anthropic Claude' },
  { value: 'ALTRO', label: 'Altro' },
];

// Modelli suggeriti per provider — il primo è il default alla creazione
const MODELLI_PER_PROVIDER: Record<string, { value: string; label: string }[]> = {
  GEMINI: [
    { value: 'gemini-2.5-pro-preview-03-25', label: 'Gemini 2.5 Pro Preview' },
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
    { value: 'gemini-flash-latest', label: 'Gemini Flash (latest)' },
    { value: '__custom__', label: 'Personalizzato…' },
  ],
  OPENAI: [
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    { value: '__custom__', label: 'Personalizzato…' },
  ],
  ANTHROPIC: [
    { value: 'claude-opus-4-5', label: 'Claude Opus 4.5' },
    { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    { value: '__custom__', label: 'Personalizzato…' },
  ],
  ALTRO: [
    { value: '__custom__', label: 'Personalizzato…' },
  ],
};

function getDefaultModello(provider: string): string {
  const lista = MODELLI_PER_PROVIDER[provider];
  if (!lista || lista.length === 0) return '';
  // primo elemento non-custom
  const primo = lista.find(m => m.value !== '__custom__');
  return primo ? primo.value : '';
}

const CONTESTO_OPTIONS = [
  { value: 'CONTRATTO_INSEGNA', label: 'Contratto Insegna' },
];

export default function IntegrazioneAIPage() {
  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const [modalSocietaId, setModalSocietaId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'config' | 'log'>('config');

  // Tab Configurazione
  const [configurazioni, setConfigurazioni] = useState<ConfigurazioneAI[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<ModalData>({
    provider: 'GEMINI',
    modello: 'gemini-1.5-pro',
    apiKey: '',
    note: '',
    attivo: false,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState<Record<number, TestResult>>({});
  const [testing, setTesting] = useState<Record<number, boolean>>({});
  const [modelloCustom, setModelloCustom] = useState(''); // usato quando select = '__custom__'
  const [modelloSelect, setModelloSelect] = useState(''); // valore select modello
  const [importOpen, setImportOpen] = useState(false);

  // Tab Log
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [logFilters, setLogFilters] = useState({
    dal: '',
    al: '',
    contestoTipo: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  // Carica configurazioni
  const loadConfigurazioni = useCallback(async () => {
    if (!pronta) return;

    try {
      setLoadingConfig(true);
      const p = new URLSearchParams();
      if (filterSocietaId) p.append('societaId', filterSocietaId);

      const data = await apiFetch<ConfigurazioneAI[]>(`/api/impostazioni/ai/configurazioni?${p}`);
      setConfigurazioni(data);
    } catch (err) {
      console.error('Errore caricamento configurazioni:', err);
    } finally {
      setLoadingConfig(false);
    }
  }, [pronta, filterSocietaId]);

  useEffect(() => {
    if (activeTab === 'config') {
      loadConfigurazioni();
    }
  }, [activeTab, loadConfigurazioni]);

  // Carica log
  const loadLog = useCallback(async () => {
    if (!pronta) return;

    try {
      setLoadingLog(true);
      const p = new URLSearchParams();
      if (filterSocietaId) p.append('societaId', filterSocietaId);
      if (logFilters.dal) p.append('dal', logFilters.dal);
      if (logFilters.al) p.append('al', logFilters.al);
      if (logFilters.contestoTipo) p.append('contestoTipo', logFilters.contestoTipo);
      p.append('page', pagination.page.toString());
      p.append('limit', pagination.limit.toString());

      const data = await apiFetch<{ log: LogEntry[]; pagination: typeof pagination }>(
        `/api/impostazioni/ai/log?${p}`
      );
      setLogEntries(data.log);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Errore caricamento log:', err);
    } finally {
      setLoadingLog(false);
    }
  }, [pronta, filterSocietaId, logFilters, pagination.page, pagination.limit]);

  useEffect(() => {
    if (activeTab === 'log') {
      loadLog();
    }
  }, [activeTab, loadLog]);

  const handleCreate = () => {
    const defaultProvider = 'GEMINI';
    const defaultModello = getDefaultModello(defaultProvider);
    setModalSocietaId(filterSocietaId);
    setModalData({
      provider: defaultProvider,
      modello: defaultModello,
      apiKey: '',
      note: '',
      attivo: false,
    });
    setModelloSelect(defaultModello);
    setModelloCustom('');
    setError('');
    setShowModal(true);
  };

  const handleEdit = (config: ConfigurazioneAI) => {
    const listaModelli = MODELLI_PER_PROVIDER[config.provider] || [];
    const isKnown = listaModelli.some(m => m.value === config.modello && m.value !== '__custom__');
    setModalSocietaId(filterSocietaId);
    setModalData({
      id: config.id,
      provider: config.provider,
      modello: config.modello,
      apiKey: '',
      note: config.note || '',
      attivo: config.attivo,
    });
    setModelloSelect(isKnown ? config.modello : '__custom__');
    setModelloCustom(isKnown ? '' : config.modello);
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!modalData.provider || !modalData.modello) {
      setError('Provider e modello sono obbligatori');
      return;
    }

    if (!modalData.id && !modalData.apiKey) {
      setError('API Key è obbligatoria per una nuova configurazione');
      return;
    }

    if (!modalSocietaId) {
      setError('Società non selezionata');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload: any = {
        societaId: parseInt(modalSocietaId, 10),
        provider: modalData.provider,
        modello: modalData.modello,
        note: modalData.note || null,
        attivo: modalData.attivo,
      };

      // Includi apiKey solo se è stata fornita
      if (modalData.apiKey) {
        payload.apiKey = modalData.apiKey;
      }

      if (modalData.id) {
        // Modifica
        await apiFetch(`/api/impostazioni/ai/configurazioni/${modalData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // Creazione
        await apiFetch('/api/impostazioni/ai/configurazioni', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      setShowModal(false);
      loadConfigurazioni();
    } catch (err: any) {
      if (err.message?.includes('409') || err.message?.toLowerCase().includes('già esiste')) {
        setError('Esiste già una configurazione per questo provider');
      } else {
        setError(err.message || 'Errore nel salvataggio della configurazione');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (config: ConfigurazioneAI) => {
    if (!window.confirm(`Confermi l'eliminazione della configurazione ${config.provider}?`)) {
      return;
    }

    try {
      await apiFetch(`/api/impostazioni/ai/configurazioni/${config.id}`, { method: 'DELETE' });
      loadConfigurazioni();
    } catch (err: any) {
      alert(err.message || 'Errore nell\'eliminazione della configurazione');
    }
  };

  const handleTest = async (configId: number) => {
    setTesting((prev) => ({ ...prev, [configId]: true }));

    try {
      const result = await apiFetch<TestResult>(
        `/api/impostazioni/ai/configurazioni/${configId}/testa-connessione`,
        { method: 'POST' }
      );
      setTestResults((prev) => ({ ...prev, [configId]: result }));
    } catch (err: any) {
      setTestResults((prev) => ({
        ...prev,
        [configId]: {
          ok: false,
          latenzaMs: 0,
          errore: err.message || 'Errore nel test',
        },
      }));
    } finally {
      setTesting((prev) => ({ ...prev, [configId]: false }));
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calcola riepilogo log
  const logSummary = {
    totaleChiamate: logEntries.length,
    totaleToken: logEntries.reduce((sum, e) => sum + (e.promptTokens || 0) + (e.completionTokens || 0), 0),
    chiamateErrore: logEntries.filter((e) => !e.esitoOk).length,
  };

  // ── Import da altra società ─────────────────────────────────────────────
  const nomeSocietaDestinazione =
    societaList.find((s) => String(s.id) === String(filterSocietaId))?.nome || '';

  const caricaSocietaImport = useCallback(async () => {
    // @api GET /api/impostazioni/importa/ai/societa -> {"success":true,"data":[{"id":1,"nome":"Pianinvest","conteggio":1}]}
    const r = await apiFetch<{ data: SocietaSorgente[] }>('/api/impostazioni/importa/ai/societa');
    return r.data;
  }, []);

  const caricaAiSorgente = async (sorgenteId: number) => {
    // @api GET /api/impostazioni/importa/ai?sorgenteId=1 -> {"success":true,"data":[{"id":4,"provider":"GEMINI","modello":"gemini-1.5-pro","attivo":true,"note":null}]}
    const r = await apiFetch<{ data: AiSorgente[] }>(`/api/impostazioni/importa/ai?sorgenteId=${sorgenteId}`);
    return r.data;
  };

  const importaAi = async (ids: number[]) => {
    // @api POST /api/impostazioni/importa/ai {"societaId":2,"ids":[4]} -> {"success":true,"data":{"esiti":[{"id":4,"nome":"GEMINI · gemini-1.5-pro","stato":"IMPORTATO","motivo":null,"attivo":true,"nota":null}]}}
    const r = await apiFetch<{ data: { esiti: EsitoImport[] } }>('/api/impostazioni/importa/ai', {
      method: 'POST',
      body: JSON.stringify({ societaId: parseInt(filterSocietaId, 10), ids }),
    });
    return r.data.esiti;
  };

  // Messaggio di cortesia
  if (!pronta) {
    return (
      <Layout>
        <div className="page-header">
          <div className="page-header-text">
            <h2>Integrazione AI</h2>
            <p>Configurazione provider AI e log chiamate</p>
          </div>
        </div>
        <div className="bop-card">
          <div className="ai-empty-state">
            <h3 className="ai-empty-title">Seleziona una società</h3>
            <p className="ai-empty-text">
              Seleziona una società dalla barra in alto per configurare l'integrazione AI
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Integrazione AI</h2>
          <p>Configurazione provider AI e log chiamate</p>
        </div>
      </div>

      {/* Filtro Società */}
      {!isUnica && (
        <div className="bop-card">
          <div className="ai-filter-row">
            <div className="ai-filter-field">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={filterSocietaId}
                onChange={(e) => setFilterSocietaId(e.target.value)}
              >
                <option value="">Tutte le società</option>
                {societaList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bop-card">
        <div className="ai-tab-container">
          <button
            onClick={() => setActiveTab('config')}
            className={`ai-tab ${activeTab === 'config' ? 'active' : ''}`}
          >
            Configurazione Provider
          </button>
          <button
            onClick={() => setActiveTab('log')}
            className={`ai-tab ${activeTab === 'log' ? 'active' : ''}`}
          >
            Log Chiamate AI
          </button>
        </div>
      </div>

      {/* Tab Content: Configurazione */}
      {activeTab === 'config' && (
        <>
          <div className="bop-card">
            <div className="ai-card-header">
              <h3 className="ai-card-title">Provider Configurati</h3>
              <div className="bop-flex">
                <button
                  className="bop-btn-secondary"
                  onClick={() => setImportOpen(true)}
                  disabled={!filterSocietaId}
                  title={filterSocietaId ? 'Copia una configurazione AI da un\'altra società del gruppo' : 'Seleziona prima la società di destinazione'}
                >
                  ⤓ Importa da altra società
                </button>
                <button className="bop-btn-primary" onClick={handleCreate}>
                  <PlusIcon />
                  Aggiungi Provider
                </button>
              </div>
            </div>
          </div>

          <div className="bop-card">
            {loadingConfig ? (
              <div className="ai-loading">
                <p className="ai-loading-text">Caricamento...</p>
              </div>
            ) : configurazioni.length === 0 ? (
              <div className="ai-empty-state">
                <h3 className="ai-empty-title">Nessun provider configurato</h3>
                <p className="ai-empty-text">
                  Inizia configurando il primo provider AI per questa società
                </p>
              </div>
            ) : (
              <div className="bop-table-wrapper">
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Modello</th>
                      <th>Stato</th>
                      <th>Note</th>
                      <th className="ai-actions-cell">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configurazioni.map((config) => {
                      const testResult = testResults[config.id];
                      const isTesting = testing[config.id];

                      return (
                        <tr key={config.id}>
                          <td>
                            <strong>{config.provider}</strong>
                            {!!config.condivisoCon && (
                              <>
                                {' '}
                                <span className="bop-badge bop-badge-info" title="Cambiare la chiave qui la cambia anche nelle altre società che la usano">
                                  Chiave condivisa con {config.condivisoCon} {config.condivisoCon === 1 ? 'altra configurazione' : 'altre configurazioni'}
                                </span>
                              </>
                            )}
                          </td>
                          <td>{config.modello}</td>
                          <td>
                            <span className={`ai-status-badge ${config.attivo ? 'active' : 'inactive'}`}>
                              {config.attivo ? 'ATTIVO' : 'INATTIVO'}
                            </span>
                          </td>
                          <td className="ai-table-note">{config.note || '-'}</td>
                          <td>
                            <div className="actions">
                              <button
                                className="bop-btn-icon"
                                onClick={() => handleTest(config.id)}
                                title="Testa connessione"
                                disabled={isTesting}
                              >
                                {isTesting ? '...' : <ZapIcon />}
                              </button>
                              <button
                                className="bop-btn-icon"
                                onClick={() => handleEdit(config)}
                                title="Modifica"
                              >
                                <Edit2Icon />
                              </button>
                              <button
                                className="bop-btn-icon"
                                onClick={() => handleDelete(config)}
                                title="Elimina"
                              >
                                <TrashIcon />
                              </button>
                            </div>
                            {testResult && (
                              <div className={`ai-test-result ${testResult.ok ? 'success' : 'error'}`}>
                                {testResult.ok ? <CheckCircleIcon /> : <XCircleIcon />}
                                <span>
                                  {testResult.ok
                                    ? `OK (${testResult.latenzaMs}ms)`
                                    : testResult.errore}
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Tab Content: Log */}
      {activeTab === 'log' && (
        <>
          {/* Filtri Log */}
          <div className="bop-card">
            <div className="ai-log-filters">
              <div className="ai-log-filter-field-sm">
                <label className="bop-label">Dal</label>
                <input
                  type="date"
                  className="bop-input"
                  value={logFilters.dal}
                  onChange={(e) => setLogFilters({ ...logFilters, dal: e.target.value })}
                />
              </div>
              <div className="ai-log-filter-field-sm">
                <label className="bop-label">Al</label>
                <input
                  type="date"
                  className="bop-input"
                  value={logFilters.al}
                  onChange={(e) => setLogFilters({ ...logFilters, al: e.target.value })}
                />
              </div>
              <div className="ai-log-filter-field-md">
                <label className="bop-label">Tipo Contesto</label>
                <select
                  className="bop-select"
                  value={logFilters.contestoTipo}
                  onChange={(e) => setLogFilters({ ...logFilters, contestoTipo: e.target.value })}
                >
                  <option value="">Tutti</option>
                  {CONTESTO_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="bop-btn-secondary"
                onClick={() => setLogFilters({ dal: '', al: '', contestoTipo: '' })}
              >
                Reset
              </button>
            </div>
          </div>

          {/* Riepilogo Log */}
          <div className="bop-card">
            <div className="ai-stats-grid">
              <div>
                <div className="ai-stat-label">Totale Chiamate</div>
                <div className="ai-stat-value">{pagination.total}</div>
              </div>
              <div>
                <div className="ai-stat-label">
                  Totale Token (Pagina Corrente)
                </div>
                <div className="ai-stat-value primary">{logSummary.totaleToken}</div>
              </div>
              <div>
                <div className="ai-stat-label">
                  Chiamate con Errore (Pagina Corrente)
                </div>
                <div className="ai-stat-value danger">
                  {logSummary.chiamateErrore}
                </div>
              </div>
            </div>
            <p className="ai-stats-note">
              ℹ️ I token sono il dato grezzo per il calcolo dei costi. Consulta il pricing del provider per il costo effettivo.
            </p>
          </div>

          {/* Tabella Log */}
          <div className="bop-card">
            {loadingLog ? (
              <div className="ai-loading">
                <p className="ai-loading-text">Caricamento...</p>
              </div>
            ) : logEntries.length === 0 ? (
              <div className="ai-empty-state">
                <h3 className="ai-empty-title">Nessun log disponibile</h3>
                <p className="ai-empty-text">Non ci sono chiamate AI registrate per i filtri selezionati</p>
              </div>
            ) : (
              <>
                <div className="bop-table-wrapper">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Provider</th>
                        <th>Modello</th>
                        <th>Contesto</th>
                        <th className="bop-text-right">Durata (ms)</th>
                        <th className="bop-text-right">Token Prompt</th>
                        <th className="bop-text-right">Token Risposta</th>
                        <th>Esito</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td className="ai-log-date">{formatDate(entry.createdAt)}</td>
                          <td>{entry.provider}</td>
                          <td className="ai-log-model">{entry.modello}</td>
                          <td>{entry.contestoTipo}</td>
                          <td className="ai-log-number">
                            {entry.durataMs || '-'}
                          </td>
                          <td className="ai-log-number">
                            {entry.promptTokens || '-'}
                          </td>
                          <td className="ai-log-number">
                            {entry.completionTokens || '-'}
                          </td>
                          <td>
                            {entry.esitoOk ? (
                              <span className="ai-log-status success">
                                <CheckCircleIcon /> OK
                              </span>
                            ) : (
                              <span
                                className="ai-log-status error"
                                title={entry.erroreMessaggio || undefined}
                              >
                                <XCircleIcon /> Errore
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginazione */}
                {pagination.totalPages > 1 && (
                  <div className="ai-pagination">
                    <button
                      className="bop-btn-secondary"
                      onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                      disabled={pagination.page === 1}
                    >
                      Precedente
                    </button>
                    <span className="ai-pagination-info">
                      Pagina {pagination.page} di {pagination.totalPages}
                    </span>
                    <button
                      className="bop-btn-secondary"
                      onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      Successiva
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Modale Configurazione */}
      {showModal && (
        <div className="bop-modal-overlay">
          <div className="modal ai-modal-size" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalData.id ? 'Modifica Provider' : 'Aggiungi Provider'}</h3>
              <button className="bop-btn-icon" onClick={() => setShowModal(false)}>
                <XIcon />
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="alert alert-error ai-modal-error">
                  {error}
                </div>
              )}

              {/* Società */}
              {!isUnica && (
                <div className="bop-form-group">
                  <label className="bop-label">
                    Società <span className="ai-field-required">*</span>
                  </label>
                  <select className="bop-select" value={modalSocietaId} disabled>
                    <option value="">Seleziona società...</option>
                    {societaList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nome}
                      </option>
                    ))}
                  </select>
                  <small className="ai-field-hint">
                    La società è stata bloccata all'apertura della modale
                  </small>
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-label">
                  Provider <span className="ai-field-required">*</span>
                </label>
                <select
                  className="bop-select"
                  value={modalData.provider}
                  onChange={(e) => {
                    const newProvider = e.target.value;
                    const newDefault = getDefaultModello(newProvider);
                    setModalData({ ...modalData, provider: newProvider, modello: newDefault });
                    setModelloSelect(newDefault);
                    setModelloCustom('');
                  }}
                  disabled={!!modalData.id}
                >
                  {PROVIDER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {modalData.id && (
                  <small className="ai-field-hint">
                    Il provider non può essere modificato
                  </small>
                )}
              </div>

              <div className="bop-form-group">
                <label className="bop-label">
                  Modello <span className="ai-field-required">*</span>
                </label>
                <select
                  className="bop-select"
                  value={modelloSelect}
                  onChange={(e) => {
                    const v = e.target.value;
                    setModelloSelect(v);
                    if (v !== '__custom__') {
                      setModalData({ ...modalData, modello: v });
                      setModelloCustom('');
                    } else {
                      setModalData({ ...modalData, modello: '' });
                    }
                  }}
                >
                  {(MODELLI_PER_PROVIDER[modalData.provider] || [{ value: '__custom__', label: 'Personalizzato…' }]).map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                {modelloSelect === '__custom__' && (
                  <input
                    type="text"
                    className="bop-input"
                    value={modelloCustom}
                    onChange={(e) => {
                      setModelloCustom(e.target.value);
                      setModalData({ ...modalData, modello: e.target.value });
                    }}
                    placeholder="Inserisci il nome esatto del modello..."
                    style={{ marginTop: '6px' }}
                    autoFocus
                  />
                )}
                <small className="ai-field-hint">
                  Usa il nome esatto accettato dall'API del provider
                </small>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">
                  API Key {!modalData.id && <span className="ai-field-required">*</span>}
                </label>
                <input
                  type="password"
                  className="bop-input"
                  value={modalData.apiKey}
                  onChange={(e) => setModalData({ ...modalData, apiKey: e.target.value })}
                  placeholder={modalData.id ? 'Lascia vuoto per non modificare' : 'Inserisci API Key...'}
                />
                <small className="ai-field-hint">
                  ⚠️ La chiave viene salvata in GCP Secret Manager e non sarà mai visibile dopo il salvataggio
                </small>
                {!!modalData.id && !!configurazioni.find((c) => c.id === modalData.id)?.condivisoCon && (
                  <small className="ai-field-hint">
                    ⚠️ Questa chiave è condivisa con altre società: se la cambi qui, cambia anche per loro.
                  </small>
                )}
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Note</label>
                <textarea
                  className="bop-input"
                  value={modalData.note}
                  onChange={(e) => setModalData({ ...modalData, note: e.target.value })}
                  rows={3}
                  placeholder="Note opzionali..."
                />
              </div>

              <div className="bop-form-group">
                <label className="ai-checkbox-label">
                  <input
                    type="checkbox"
                    checked={modalData.attivo}
                    onChange={(e) => setModalData({ ...modalData, attivo: e.target.checked })}
                  />
                  <span>Attiva questa configurazione</span>
                </label>
                <small className="ai-field-hint">
                  Attivando questa configurazione, le altre verranno disattivate
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button className="bop-btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                Annulla
              </button>
              <button className="bop-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : modalData.id ? 'Salva modifiche' : 'Crea configurazione'}
              </button>
            </div>
          </div>
        </div>
      )}

      {importOpen && (
        <ImportaDaSocietaModal<AiSorgente>
          titolo="Importa configurazione AI da altra società"
          destinazioneId={filterSocietaId}
          nomeDestinazione={nomeSocietaDestinazione}
          intro={
            <>
              Le configurazioni vengono copiate in <strong>{nomeSocietaDestinazione}</strong> usando
              la <strong>stessa chiave API</strong> della società di origine: le due società la
              condivideranno, e cambiarla in una la cambia anche nell&apos;altra. Se la società ha già
              un provider uguale, quella configurazione viene saltata.
            </>
          }
          testoRicerca={(c) => `${c.provider} ${c.modello} ${c.note || ''}`}
          colonne={[
            { titolo: 'Provider', valore: (c) => <strong>{c.provider}</strong> },
            { titolo: 'Modello', valore: (c) => c.modello },
            { titolo: 'Stato', valore: (c) => <span className={`bop-badge ${c.attivo ? 'bop-badge-success' : 'bop-badge-neutral'}`}>{c.attivo ? 'Attivo' : 'Inattivo'}</span> },
            { titolo: 'Note', valore: (c) => <span className="bop-text-muted">{c.note || '—'}</span> },
          ]}
          caricaSocieta={caricaSocietaImport}
          caricaElementi={caricaAiSorgente}
          importa={importaAi}
          notaEsito="Conviene provare la connessione della configurazione importata con il pulsante ⚡."
          onChiudi={() => setImportOpen(false)}
          onImportato={loadConfigurazioni}
        />
      )}
    </Layout>
  );
}
